import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'

// Define the config interface
interface AppConfig {
  BACKEND_URL: string;
}

// Define traffic source types (limit to supported backend values)
type TrafficSource = 'tiktok' | 'facebook' | 'none';

// Declare global window object with config and traffic source
declare global {
  interface Window {
    appConfig: AppConfig;
    trafficSource: TrafficSource;
    fbq: any; // Meta Pixel function
    ttq: any; // TikTok Pixel function
  }
}

// Function to detect traffic source
function detectTrafficSource(): TrafficSource {
  const urlParams = new URLSearchParams(window.location.search);

  // 1) UTM parameters (primary signal)
  const utmSource = urlParams.get('utm_source')?.toLowerCase() || '';
  if (utmSource) {
    if (utmSource.includes('tiktok')) return 'tiktok';
    if (utmSource.includes('facebook') || utmSource.includes('instagram') || utmSource.includes('meta')) return 'facebook';
    // If UTM is present but not our channels, ignore and continue
  }

  // 2) Click IDs common on mobile/in-app
  const hasTtclid = !!urlParams.get('ttclid');
  const hasFbclid = !!urlParams.get('fbclid');
  if (hasTtclid) return 'tiktok';
  if (hasFbclid) return 'facebook';

  // 3) Referrer (secondary signal)
  const referrer = (document.referrer || '').toLowerCase();
  if (referrer) {
    if (referrer.includes('tiktok.com')) return 'tiktok';
    if (
      referrer.includes('facebook.com') ||
      referrer.includes('instagram.com') ||
      referrer.includes('fb.com') ||
      referrer.includes('l.facebook.com') ||
      referrer.includes('lm.facebook.com') ||
      referrer.includes('m.facebook.com')
    ) {
      return 'facebook';
    }
  }

  // 4) In-app browser user-agent hints (last resort)
  const ua = (navigator.userAgent || '').toLowerCase();
  if (ua.includes('tiktok')) return 'tiktok';
  if (ua.includes('fban') || ua.includes('fbav') || ua.includes('instagram')) return 'facebook';

  // 5) Unknown -> none
  return 'none';
}

// Function to track traffic source to analytics
function trackTrafficSource(source: TrafficSource) {
  // Track to Meta Pixel
  if (window.fbq) {
    window.fbq('track', 'PageView', {
      traffic_source: source,
      utm_source: source
    });
  }

  // Track to TikTok Pixel
  if (window.ttq) {
    window.ttq.track('PageView', {
      traffic_source: source,
      utm_source: source
    });
  }
}

// Load configuration before rendering the app
async function loadConfig(): Promise<AppConfig> {
  try {
    const response = await fetch('/config.json');
    if (response.ok) {
      const config = await response.json();
      return config;
    }
  } catch (error) {
    console.warn('Failed to load config.json, using defaults:', error);
  }

  return {
    BACKEND_URL: 'http://localhost:5001'
  };
}

// Initialize app with config and traffic tracking
async function initializeApp() {
  const config = await loadConfig();
  window.appConfig = config;

  // Detect and track traffic source
  const trafficSource = detectTrafficSource();
  window.trafficSource = trafficSource;

  // Track the traffic source
  trackTrafficSource(trafficSource);

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

initializeApp();
